// features/hives/lib/useNewHive.ts
import { useApiariesStore } from "entities/apiaries";
import { useHivesStore } from "entities/hives";
import { useForm, useWatch } from "react-hook-form";
import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";
import { $api } from "shared/api";

export type NewHiveFormValues = {
    number: string | number | null;
    uterusYear: number | null;
    uterusMark: string | number | null;
    uterusMarkColor: number;
    breed: string;
};

const DEFAULTS: NewHiveFormValues = {
    number: "",
    uterusYear: new Date().getFullYear(),
    uterusMark: "",
    uterusMarkColor: 0,
    breed: "",
};

export const useNewHive = (defaults?: Partial<NewHiveFormValues>) => {
    const apiaryId = useApiariesStore((s) => s.currentApiaryId);
    const addHive = useHivesStore((s) => s.addHive);
    const getHivesFromServer = useHivesStore((s) => s.getHivesFromServer);
    const hives = useHivesStore((s) => s.hives);

    const {
        control,
        handleSubmit,
        setValue,
        reset,
        clearErrors,
        setError,
        formState: { errors },
        getValues,
    } = useForm<NewHiveFormValues>({
        defaultValues: { ...DEFAULTS, ...(defaults || {}) },
        mode: "onSubmit",
    });

    const watchMark = useWatch({ control, name: "uterusMark" });
    const watchBreed = useWatch({ control, name: "breed" });

    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isNumberDuplicate, setIsNumberDuplicate] = useState(false);
    const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const lastCheckedRef = useRef<string>("");

    const [markOptions, setMarkOptions] = useState<string[]>([]);
    const [breedOptions, setBreedOptions] = useState<string[]>([]);
    const [markOptionsShown, setMarkOptionsShown] = useState(false);
    const [breedOptionsShown, setBreedOptionsShown] = useState(false);

    const fetchDict = async (type: "UTERUS_MARK" | "UTERUS_BREED") => {
        if (!apiaryId) return [];
        try {
            const http = await $api.get<any>("/dictionaries", { params: { apiaryId: String(apiaryId), type } });
            const arr: any[] = Array.isArray(http?.data) ? http.data : [];
            return arr.filter((el) => el.type === type).map((d) => String(d?.name ?? "")).filter(Boolean);
        } catch {
            return [];
        }
    };

    useEffect(() => {
        const load = async () => {
            const [marks, breeds] = await Promise.all([fetchDict("UTERUS_MARK"), fetchDict("UTERUS_BREED")]);
            setMarkOptions(marks);
            setBreedOptions(breeds);
        };
        load();
    }, [apiaryId]);

    const filteredMarkOptions = useMemo(() => {
        const q = String(watchMark ?? "").toLowerCase();
        if (!q) return markOptions;
        return markOptions.filter((n) => n.toLowerCase().includes(q));
    }, [markOptions, watchMark]);

    const filteredBreedOptions = useMemo(() => {
        const q = String(watchBreed ?? "").toLowerCase();
        if (!q) return breedOptions;
        return breedOptions.filter((n) => n.toLowerCase().includes(q));
    }, [breedOptions, watchBreed]);

    const onMarkFocus = () => setMarkOptionsShown(true);
    const onBreedFocus = () => setBreedOptionsShown(true);

    const onMarkBlur = (_e: React.FocusEvent<HTMLInputElement>, field: any) => {
        setTimeout(() => setMarkOptionsShown(false), 100);
        field.onBlur?.();
    };
    const onBreedBlur = (_e: React.FocusEvent<HTMLInputElement>, field: any) => {
        setTimeout(() => setBreedOptionsShown(false), 100);
        field.onBlur?.();
    };

    const onPickMarkOption = (val: string, field: any) => {
        field.onChange(val);
        setValue("uterusMark", val);
        setMarkOptionsShown(false);
    };
    const onPickBreedOption = (val: string, field: any) => {
        field.onChange(val);
        setValue("breed", val);
        setBreedOptionsShown(false);
    };

    const onNumberChange = (v: string) => {
        if (!apiaryId) {
            setValue("number", v);
            return;
        }
        if (debounceRef.current) {
            clearTimeout(debounceRef.current);
            debounceRef.current = null;
        }

        if (!v || v.trim() === "") {
            setIsNumberDuplicate(false);
            clearErrors("number");
            setValue("number", "");
            return;
        }

        debounceRef.current = setTimeout(async () => {
            const numStr = v.trim();
            if (!numStr) {
                setIsNumberDuplicate(false);
                clearErrors("number");
                setValue("number", "");
                return;
            }
            if (lastCheckedRef.current === numStr && isNumberDuplicate) {
                setError("number", { type: "validate", message: "Улей с таким номером уже существует" });
                return;
            }
            const existing = await useHivesStore.getState().getHivesByNumber(apiaryId, numStr);
            const found = existing.some((h) => String(h.number) === String(numStr));
            if (found) {
                setIsNumberDuplicate(true);
                lastCheckedRef.current = numStr;
                setError("number", { type: "validate", message: "Улей с таким номером уже существует" });
                return;
            }
            setIsNumberDuplicate(false);
            clearErrors("number");
            lastCheckedRef.current = numStr;
            setValue("number", numStr);
        }, 450);
    };

    const onYearChange = (y: number | null) => setValue("uterusYear", y);
    const onMarkNumberChange = (v: string) => {
        setValue("uterusMark", v);
        setMarkOptionsShown(true);
    };
    const onMarkColorChange = (id: string) => setValue("uterusMarkColor", (id as unknown) as number);
    const onBreedChange = (v: string) => {
        setValue("breed", v);
        setBreedOptionsShown(true);
    };

    const ensureDictionaryItem = async (type: "UTERUS_MARK" | "UTERUS_BREED", name: string) => {
        if (!apiaryId) return;
        const list = type === "UTERUS_MARK" ? markOptions : breedOptions;
        const normalized = String(name).trim();
        if (!normalized || list.includes(normalized)) return;
        try {
            const body = { apiaryId: String(apiaryId), name: normalized, type };
            const res = await $api.post("/dictionaries", body);
            if (res?.status === 200 || res?.status === 201) {
                if (type === "UTERUS_MARK") setMarkOptions((p) => [...p, normalized]);
                else setBreedOptions((p) => [...p, normalized]);
            }
        }  catch (err) {
            console.log(err)
        }
    };

    const onSubmit = async (data: NewHiveFormValues) => {
        if (!apiaryId) {
            toast.error("Не выбран точок (пасека)");
            return null;
        }
        if (!data.number || String(data.number).trim() === "") {
            toast.error("Введите номер улья");
            return null;
        }
        if (isNumberDuplicate || errors.number) {
            toast.error("Улей с таким номером уже существует");
            return null;
        }

        try {
            const dupCheck = await useHivesStore.getState().getHivesByNumber(apiaryId, String(data.number));
            if (dupCheck.some((h) => String(h.number) === String(data.number))) {
                setIsNumberDuplicate(true);
                setError("number", { type: "validate", message: "Улей с таким номером уже существует" });
                toast.error("Улей с таким номером уже существует");
                return null;
            }
        } catch (err) {
            console.log(err)
        }

        const payload = {
            number: Number(data.number),
            apiaryId: String(apiaryId),
            uterus: {
                year: data.uterusYear ? Number(data.uterusYear) : 0,
                mark: data.uterusMark ? Number(data.uterusMark) : 0,
                breed: data.breed?.trim() || "",
                color: String(data.uterusMarkColor),
            },
            frames: 0,
            strength: 0,
            brood: 0,
            devices: 0,
            happiness: 0,
            isSmart: false,
        };

        try {
            setIsSubmitting(true);

            const markStr = String(getValues("uterusMark") ?? "").trim();
            const breedStr = String(getValues("breed") ?? "").trim();

            await Promise.all([
                markStr ? ensureDictionaryItem("UTERUS_MARK", markStr) : Promise.resolve(),
                breedStr ? ensureDictionaryItem("UTERUS_BREED", breedStr) : Promise.resolve(),
            ]);

            const res = await addHive(payload);
            if (res?.status === 200 || res?.status === 201) {
                toast.success("Улей создан");
                if (window.ym) window.ym(111273960,'reachGoal','create_hive')
                await getHivesFromServer(
                    { apiaryId, page: { index: 0, size: Math.max(10, (hives?.length || 0) + 1) } } as any,
                    true
                );
            } else {
                toast.error("Не удалось создать улей");
            }
            return res;
        } catch {
            toast.error("Ошибка запроса. Попробуйте позже");
            return null;
        } finally {
            setIsSubmitting(false);
        }
    };

    const doReset = () => {
        if (debounceRef.current) {
            clearTimeout(debounceRef.current);
            debounceRef.current = null;
        }
        setIsNumberDuplicate(false);
        setMarkOptionsShown(false);
        setBreedOptionsShown(false);
        reset({ ...DEFAULTS, ...(defaults || {}) });
        clearErrors("number");
    };

    return {
        control,
        errors,
        isSubmitting,
        onNumberChange,
        onYearChange,
        onMarkNumberChange,
        onMarkColorChange,
        onBreedChange,
        handleSubmit,
        onSubmit,
        doReset,

        markOptionsShown,
        breedOptionsShown,
        filteredMarkOptions,
        filteredBreedOptions,
        onPickMarkOption,
        onPickBreedOption,
        onMarkFocus,
        onBreedFocus,
        onMarkBlur,
        onBreedBlur,
    };
};
